import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import localSettings from "@app/config/LocalSettings";
import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import { getNewRoutesDrillDownViewState } from "@app/scene/dashboard/store/cards/newRoutes/selectors";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { callApi } from "@app/store/api/callApi";
import { fetchNewRoutesCardDataHelper } from "@app/store/dashboard/cardDataFetchHelpers";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import NewRoutesCardConfiguration from "@app/types/dashboard/cards/newRoutes/NewRoutesCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import FieldModel from "@app/util/fieldModel/impl/FieldModel";
import { RouteFieldName } from "@app/util/fieldModel/RouteFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

import {
  applyNewRoutesDrillDownListViewSettings,
  fetchNewRoutesCardData,
  fetchNewRoutesCardDrillDownData,
  showNewRoutesDrillDown,
} from "./actions";

const defaultColumns: string[] = [
  RouteFieldName.NAME,
  RouteFieldName.START_ADDRESS,
  RouteFieldName.USER,
  RouteFieldName.CREATED_AT,
  RouteFieldName.COUNT,
];

const defaultSortColumn = RouteFieldName.CREATED_AT;

export const getFilters = (scope: Scope | undefined, configuration: NewRoutesCardConfiguration) => {
  const { dateRange } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;

  const range = getDatesRange(dateRange.range, dateRange.subRange);

  return {
    createdAt: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
    teamId: isTeamScope ? { [PlatformFilterOperator.CONTAINS]: [scope.teamId!] } : undefined,
    userId: scope?.userId ? { [PlatformFilterOperator.CONTAINS]: [scope.userId] } : undefined,
  };
};

function* onFetchNewRoutesCardData({ payload }: ReturnType<typeof fetchNewRoutesCardData>) {
  const { callback, configuration, failureCallback, scope } = payload;

  try {
    yield call(fetchNewRoutesCardDataHelper, { callback, configuration, scope });
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({ payload }: ReturnType<typeof showNewRoutesDrillDown.request>) {
  const { configuration, customDateRange, scope } = payload;
  const { dateRange } = configuration;

  const drillDownViewState: DrillDownViewState = yield select(getNewRoutesDrillDownViewState);

  const fieldModel = getFieldModelByEntityType(EntityType.ROUTE);
  const viewState = localSettings.getViewSettings(`dashboard/newRoutes`, fieldModel, {
    ...drillDownViewState,
    columns: getDefaultDrillDownColumns(defaultColumns, fieldModel),
    sort: [
      {
        field: fieldModel.getByName(defaultSortColumn)!,
        order: SortOrder.DESC,
      },
    ],
  });

  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const range = customDateRange ?? getDatesRange(dateRange.range, dateRange.subRange);

  const filter: FilterModel = createAndCondition([
    createSimpleCondition(CommonFieldName.CREATED_AT, FilterOperator.IN_RANGE, [
      range.startDate,
      range.endDate,
    ]),
  ]);
  if (isTeamScope) {
    filter.conditions.push(
      createSimpleCondition(CommonFieldName.TEAM, FilterOperator.IN_ANY, [scope.teamId!])
    );
  }
  if (scope?.userId) {
    filter.conditions.push(
      createSimpleCondition(CommonFieldName.USER, FilterOperator.IN_ANY, [scope.userId])
    );
  }
  viewState.filter = filter;

  yield put(showNewRoutesDrillDown.success({ viewState }));
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchNewRoutesCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: new routes card, onFetchDrillDownData", payload);
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyNewRoutesDrillDownListViewSettings(payload.request));
    }

    const drillDownViewState: DrillDownViewState = yield select(getNewRoutesDrillDownViewState);

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(drillDownViewState, `dashboard/newRoutes`);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const fieldModel: FieldModel = yield call(getFieldModelByEntityType, EntityType.ROUTE);
    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
          drillDownViewState.columns,
          fieldModel
        ),
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const response: AggregatedListResponse<AnyEntity> = yield callApi(
      "fetchUnifiedRoutesAggregated",
      orgId,
      requestPayload
    );
    if (payload.dataCallback) {
      payload.dataCallback(response);
    }
    yield put(
      fetchNewRoutesCardDrillDownData.success({
        totalFilteredRoutes: response.total,
        totalRoutes: response.accessible,
      })
    );
  } catch (error) {
    if (payload.failCallback) {
      payload.failCallback();
    }
    yield put(fetchNewRoutesCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

export function* newRoutesSagas() {
  // we use takeEvery because there might be several cards of such type on board
  // however, it would be more optimal to use takeLatest, but also filter by card ids
  yield takeEvery(fetchNewRoutesCardData, onFetchNewRoutesCardData);
  yield takeLatest(showNewRoutesDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchNewRoutesCardDrillDownData.request, onFetchDrillDownData);
}
